import { ActionIcon, Badge, Box, Button, Group, Modal, Progress, Table, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconRefresh, IconRestore, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { DataTable } from "mantine-datatable";
import { PropsWithChildren, useMemo } from "react";
import { CreateGenerateTestSetJobButton } from "./CreateGenerateTestSetJob";
import dayjs from "dayjs"
import * as R from "remeda";
import { TooltipMultiline } from "@/Components/TooltipMultiline";
import { useDeleteApiGenerateTestSetJobDelete, useGetApiEmbeddingCollectionList, useGetApiGenerateTestSetJobList, usePostApiGenerateTestSetJobCreate, usePostApiGenerateTestSetJobRequeue } from "@/API";
import { GroupTokenUsageByModel, SumTokenUsageDataWithModel } from "@/Helpers/TokenUsageHelper";

export function GenerateTestSetJobManagerButton(props: { workspaceId: string, testSetId: string } & PropsWithChildren) {
    const queryClient = useQueryClient()
    const [opened, { open, close }] = useDisclosure(false);
    const list = useGetApiGenerateTestSetJobList({ workspaceId: props.workspaceId, testSetId: props.testSetId })
    const collectionsList = useGetApiEmbeddingCollectionList({ workspaceId: props.workspaceId })
    const create_Mutation = usePostApiGenerateTestSetJobCreate()
    const requeue_Mutation = usePostApiGenerateTestSetJobRequeue()
    const delete_Mutation = useDeleteApiGenerateTestSetJobDelete()

    const collectionsMap = useMemo(() => {
        const map = new Map<string, { name?: string; embeddings?: Array<{ dataId?: string; description?: string }> }>();
        (collectionsList.data?.data.results ?? []).forEach(c => {
            if (c.id) map.set(c.id, c);
        });
        return map;
    }, [collectionsList.data]);

    return <>
        <div onClick={open}>
            {props.children}
        </div>
        <Modal opened={opened} onClose={close} size="100%" title="測試資料產生工作管理">
            <Group mb="md">
                <CreateGenerateTestSetJobButton workspaceId={props.workspaceId} testSetId={props.testSetId} onSubmit={async v => {
                    try {
                        v.testSetId = props.testSetId
                        await create_Mutation.mutateAsync({
                            params: {
                                workspaceId: props.workspaceId,
                            },
                            data: v
                        })
                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                        notifications.show({ message: "新增成功" })
                    } catch (e) {
                        notifications.show({ message: "新增失敗", color: "red" })
                    }
                }}>
                    <Button>
                        創建工作
                    </Button>
                </CreateGenerateTestSetJobButton>
                <ActionIcon variant="subtle" onClick={() => {
                    queryClient.invalidateQueries({ queryKey: list.queryKey })
                }}>
                    <IconRefresh style={{ width: '70%', height: '70%' }} />
                </ActionIcon>
            </Group>
            <DataTable
                withTableBorder
                minHeight={(list.data?.data.results?.length ?? 0) > 0 ? 0 : 150}
                noRecordsText="沒有資料"
                fetching={list.isFetching}
                columns={[
                    {
                        accessor: 'message', title: "訊息", render: (data) => <TooltipMultiline label={data.message}>
                            <Text w={160} truncate="end">
                                {data.message}
                            </Text>
                        </TooltipMultiline>
                    },
                    {
                        accessor: 'persona.name', title: "角色", render: (data) => <Tooltip label={data.persona?.role_description}>
                            <Text size="sm">
                                {data.persona?.name}
                            </Text>
                        </Tooltip>
                    },
                    {
                        accessor: 'status', title: "工作狀態", render: (data) => {
                            let color = "gray";
                            if (data.status === "Completed") color = "green";
                            else if (data.status === "Processing") color = "blue";
                            else if (data.status === "Error") color = "red";
                            else if (data.status === "Waiting") color = "yellow";
                            return <Badge color={color}>{data.status}</Badge>;
                        }
                    },
                    {
                        accessor: 'tokenUsage',
                        title: "Token 使用量 (In/Out)",
                        render: (data) => {
                            const allUsage = (data.processedRecord ?? []).flatMap(r => r.tokenUsage ?? []);
                            const totalUsage = SumTokenUsageDataWithModel(allUsage);
                            const groupedUsage = GroupTokenUsageByModel(allUsage);
                            const tooltipText = groupedUsage.map(x => `${x.model}: ${x.inputTokens} In / ${x.outputTokens} Out`).join('\n') || "無 Token 紀錄";
                            return (
                                <Tooltip label={tooltipText} multiline>
                                    <Text size="sm">
                                        {totalUsage.inputTokens ?? 0} / {totalUsage.outputTokens ?? 0}
                                    </Text>
                                </Tooltip>
                            );
                        }
                    },
                    { accessor: 'createdTime', title: "創建時間", render: (data) => data.createdTime ? dayjs(data.createdTime).toDate().toLocaleString() : "" },
                    { accessor: 'updatedTime', title: "更新時間", render: (data) => data.updatedTime ? dayjs(data.updatedTime).toDate().toLocaleString() : "" },
                    {
                        accessor: 'progress', title: "進度 (題數)", width: 120, render: (data) => {
                            const validRecords = (data.processedRecord ?? []).filter(x => (x.processedCount ?? 0) > 0 || !!x.errorMessage);
                            var processed = R.pipe(validRecords, R.sumBy(x => x.processedCount ?? 0));
                            var total = R.pipe(validRecords, R.sumBy(x => x.totalCount ?? 0));
                            return <Tooltip label={`已產生題數: ${processed}/${total} 題`}>
                                <Progress value={total > 0 ? 100 * processed / total : 0} />
                            </Tooltip>
                        }
                    },
                    {
                        accessor: 'actions',
                        title: <Box mr={6}>操作</Box>,
                        textAlign: 'right',
                        render: (data) => (
                            <Group gap={4} justify="right" wrap="nowrap">
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={() => {
                                        modals.openConfirmModal({
                                            title: '重新加入列隊',
                                            children: (
                                                <Text size="sm">
                                                    是否要將此工作重新加入列隊?
                                                </Text>
                                            ),
                                            labels: { confirm: '確認', cancel: '取消' },
                                            onConfirm: async () => {
                                                try {
                                                    if (data.id) {
                                                        await requeue_Mutation.mutateAsync({
                                                            params: {
                                                                workspaceId: props.workspaceId,
                                                                id: data.id
                                                            }
                                                        })
                                                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                        notifications.show({ message: "操作成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "操作失敗", color: "red" })
                                                }
                                            },
                                        });

                                    }}
                                >
                                    <IconRestore size={16} />
                                </ActionIcon>
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => {
                                        modals.openContextModal({
                                            modal: "ConfirmCountDown",
                                            title: '刪除',
                                            innerProps: {
                                                countDownSecond: 0,
                                                children: (
                                                    <Text size="sm">
                                                        是否要刪除"{data.id}"?
                                                    </Text>
                                                ),
                                                labels: {
                                                    confirm: '刪除', cancel: '取消'
                                                },
                                                confirmProps: { color: 'red' },
                                                onConfirm: async () => {
                                                    try {
                                                        if (data.id) {
                                                            await delete_Mutation.mutateAsync({
                                                                params: {
                                                                    workspaceId: props.workspaceId,
                                                                    id: data.id
                                                                }
                                                            })
                                                            queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                            notifications.show({ message: "刪除成功" })
                                                        }
                                                    } catch (e) {
                                                        notifications.show({ message: "刪除失敗", color: "red" })
                                                    }
                                                },
                                            },

                                        });
                                    }}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Group>
                        ),
                    },
                ]}
                rowExpansion={{
                    content: ({ record: job }) => {
                        const activeRecords = (job.processedRecord ?? []).filter(rec => (rec.processedCount ?? 0) > 0 || !!rec.errorMessage);
                        return (
                            <Box p="xs">
                                <Text fw={500} size="sm" mb={4}>處理紀錄明細</Text>
                                {activeRecords.length === 0 ? (
                                    <Text size="xs" c="dimmed">無紀錄</Text>
                                ) : (
                                    <Table withTableBorder withColumnBorders>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>資料集 / 檔案</Table.Th>
                                                <Table.Th>產出題數 (已產生 / 目標)</Table.Th>
                                                <Table.Th>完成狀態</Table.Th>
                                                <Table.Th>Token 使用量</Table.Th>
                                                <Table.Th>錯誤訊息</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {activeRecords.map((rec, idx) => {
                                                const usage = SumTokenUsageDataWithModel(rec.tokenUsage ?? []);
                                                const colInfo = collectionsMap.get(rec.embeddingCollectionId ?? "");
                                                const colName = rec.embeddingCollectionName || colInfo?.name || rec.embeddingCollectionId;
                                                let fileName = rec.dataName;
                                                if (!fileName && colInfo?.embeddings) {
                                                    const emb = colInfo.embeddings.find(e => e.dataId === rec.dataId);
                                                    fileName = emb?.description;
                                                }
                                                fileName = fileName || rec.dataId;

                                                return (
                                                    <Table.Tr key={idx}>
                                                        <Table.Td>
                                                            <Text size="xs" fw={500}>{fileName}</Text>
                                                            <Text size="xs" c="dimmed">{colName}</Text>
                                                        </Table.Td>
                                                        <Table.Td>{rec.processedCount ?? 0} / {rec.totalCount ?? 0} 題</Table.Td>
                                                        <Table.Td>
                                                            <Badge color={rec.isCompleted ? "green" : "gray"} size="sm">
                                                                {rec.isCompleted ? "已完成" : "未完成"}
                                                            </Badge>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Tooltip label={GroupTokenUsageByModel(rec.tokenUsage ?? []).map(x => `${x.model}: ${x.inputTokens} In / ${x.outputTokens} Out`).join('\n') || "無 Token 紀錄"}>
                                                                <Text size="xs">
                                                                    In: {usage.inputTokens ?? 0} / Out: {usage.outputTokens ?? 0}
                                                                </Text>
                                                            </Tooltip>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {rec.errorMessage ? (
                                                                <TooltipMultiline label={rec.errorMessage}>
                                                                    <Badge color="red" size="sm" style={{ cursor: 'pointer', maxWidth: 200 }}>
                                                                        <Text truncate size="xs">{rec.errorMessage}</Text>
                                                                    </Badge>
                                                                </TooltipMultiline>
                                                            ) : (
                                                                <Text size="xs" c="dimmed">無錯誤</Text>
                                                            )}
                                                        </Table.Td>
                                                    </Table.Tr>
                                                );
                                            })}
                                        </Table.Tbody>
                                    </Table>
                                )}
                            </Box>
                        );
                    }
                }}
                records={list.data?.data.results ?? []}
            />
        </Modal>
    </>
}
